import { useState, useEffect } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { ArrowUp, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/components/ui/use-toast";
import { upvoteComment, downvoteComment, getCommentUpvoteStatus } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

export interface CommentProps {
  comment: {
    id: string | number;
    content: string;
    createdAt: string;
    upvotes?: number;
    upvotes_count?: number;
    user: {
      id: string | number;
      name: string;
      username: string;
      avatar: string;
      experience?: number;
    };
    post_id?: string | number;
  };
  onReply?: (commentId: string | number) => void;
  isReply?: boolean;
  level?: number;
}

const Comment = ({ comment, onReply, isReply = false, level = 0 }: CommentProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [upvoted, setUpvoted] = useState(false);
  const [upvoteCount, setUpvoteCount] = useState(comment.upvotes_count || comment.upvotes || 0);
  const [isUpvoteLoading, setIsUpvoteLoading] = useState(false);
  
  // Check if the user has upvoted this comment and get the current upvote count
  useEffect(() => {
    const checkUpvoteStatus = async () => {
      if (!user || !comment.id) return;
      
      // Skip API calls for temporary comments (client-side only until saved)
      if (typeof comment.id === 'string' && comment.id.startsWith('temp-')) {
        setUpvoted(false);
        setUpvoteCount(comment.upvotes_count || comment.upvotes || 0);
        return;
      }
      
      try {
        const result = await getCommentUpvoteStatus(comment.id.toString(), user.id);
        setUpvoted(result.hasUpvoted);
        setUpvoteCount(result.upvoteCount);
      } catch (error) {
        console.error('Error checking upvote status:', error);
      }
    };
    
    checkUpvoteStatus();
  }, [comment.id, user]);
  
  const formatTimeAgo = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'some time ago';
    }
  };
  
  const handleUpvote = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!user) {
      toast({
        title: "Please sign in",
        description: "You need to be signed in to upvote comments",
        variant: "destructive",
      });
      return;
    }
    
    // Handle temporary comments differently
    if (typeof comment.id === 'string' && comment.id.startsWith('temp-')) {
      toast({
        title: "Comment is being saved",
        description: "You can upvote this comment once it's saved",
        variant: "default",
      });
      return;
    }
    
    setIsUpvoteLoading(true);
    
    // Optimistically update UI state
    const wasUpvoted = upvoted;
    const previousCount = upvoteCount;
    
    setUpvoted(!wasUpvoted);
    setUpvoteCount(wasUpvoted ? previousCount - 1 : previousCount + 1);
    
    try {
      if (wasUpvoted) {
        // Remove upvote
        const success = await downvoteComment(comment.id.toString(), user.id);
        if (!success) {
          // Revert UI state if API call fails
          setUpvoted(wasUpvoted);
          setUpvoteCount(previousCount);
          toast({
            title: "Error",
            description: "Failed to remove upvote. Please try again.",
            variant: "destructive",
          });
        }
      } else {
        // Add upvote
        const success = await upvoteComment(comment.id.toString(), user.id);
        if (!success) {
          // Revert UI state if API call fails
          setUpvoted(wasUpvoted);
          setUpvoteCount(previousCount);
          toast({
            title: "Error",
            description: "Failed to upvote comment. Please try again.",
            variant: "destructive",
          });
        } else if (comment.user.id !== user.id) {
          toast({
            title: "XP Awarded",
            description: `${comment.user.name} earned +1 XP from your upvote!`,
            variant: "default",
          });
        }
      }
    } catch (error) {
      console.error('Error toggling comment upvote status:', error);
      // Revert UI state if there was an exception
      setUpvoted(wasUpvoted);
      setUpvoteCount(previousCount);
      toast({
        title: "Error",
        description: "Failed to update upvote status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUpvoteLoading(false);
    }
  };

  return (
    <div className="flex gap-3" style={{ marginLeft: `${level * 20}px` }}>
      <Link to={`/profile/${comment.user.id}`}>
        <Avatar className="h-8 w-8 flex-shrink-0 border border-vibebops-highlight">
          <AvatarImage src={comment.user.avatar} alt={comment.user.name} />
          <AvatarFallback className="bg-gradient-to-r from-pink-500 to-violet-500 text-white font-bold">
            {comment.user.name.charAt(0).toUpperCase()}
          </AvatarFallback>
        </Avatar>
      </Link>
      
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Link to={`/profile/${comment.user.id}`} className="font-medium text-sm hover:underline">
            {comment.user.name}
          </Link>
          <span className="text-xs text-vibebops-textMuted">
            {formatTimeAgo(comment.createdAt)}
          </span>
        </div>
        
        <p className="text-sm mb-2">{comment.content}</p>
        
        <div className="flex items-center gap-4 text-vibebops-textMuted">
          <button 
            onClick={handleUpvote}
            disabled={isUpvoteLoading}
            className={`flex items-center gap-1 p-1 rounded-md hover:bg-vibebops-highlight ${upvoted ? 'text-vibebops-accent2' : ''}`}
            type="button"
          >
            <ArrowUp className={`h-3 w-3 ${upvoted ? 'fill-current' : ''}`} />
            <span className="text-xs">{upvoteCount}</span>
          </button>
          
          {onReply && (
            <button 
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onReply(comment.id);
              }}
              className="flex items-center gap-1 p-1 rounded-md hover:bg-vibebops-highlight"
              type="button"
            >
              <MessageCircle className="h-3 w-3" />
              <span className="text-xs">Reply</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Comment; 